// utils/StorageManager.js
// Centralized localStorage management for game state and user preferences.
//
// Keys:
//   game_settings     — Sound & UI preferences (volume, turbo, spacebar)
//   session_token     — Player session ID for cross-session validation
//   base_game_round   — Last base game round (reels, wins, bet)
//   bonus_game_round  — Mid-bonus snapshot (reels, bet, respinsLeft, totalWin, coinCount)
//   free_game_round   — Free rounds state (status, betValue, performed, count, totalWin)

import { Settings } from "../settings";

// This game's own keys (2026-10-21): the platform serves its games from ONE
// origin and they share one localStorage — with bare names one game read
// another's state. Settings.APP.STORAGE_PREFIX comes from GAME_NAME.
// ...and this player's own, as the platform's other games key theirs (their
// storage id + cache_player_id): `<Game>.<cache_player_id>.<key>`.
const PLAYER_ID = window.__OPTIONS__?.cache_player_id;
const KEY_PREFIX = PLAYER_ID
  ? `${Settings.APP.STORAGE_PREFIX}${PLAYER_ID}.`
  : Settings.APP.STORAGE_PREFIX;
// const STORAGE_KEY = "game_settings";
const STORAGE_KEY = `${KEY_PREFIX}game_settings`;
// const BASE_GAME_ROUND_KEY = "base_game_round";
const BASE_GAME_ROUND_KEY = `${KEY_PREFIX}base_game_round`;
// const SESSION_KEY = "session_token";
const SESSION_KEY = `${KEY_PREFIX}session_token`;
// const BONUS_GAME_ROUND_KEY = "bonus_game_round";
const BONUS_GAME_ROUND_KEY = `${KEY_PREFIX}bonus_game_round`;
// const FREE_GAME_ROUND_KEY = "free_game_round";
const FREE_GAME_ROUND_KEY = `${KEY_PREFIX}free_game_round`;
// const TRIGGER_WIN_KEY = "trigger_win";
const TRIGGER_WIN_KEY = `${KEY_PREFIX}trigger_win`;
// The Mad Labs free-spins trigger round — the board, its synthetic scatter win
// and the gamble package. Kept apart from base_game_round because that key is
// stripped and cleared on paths unrelated to this feature.
// const FREE_SPINS_ROUND_KEY = "free_spins_round";
const FREE_SPINS_ROUND_KEY = `${KEY_PREFIX}free_spins_round`;
// Cleopatra's Fire: the logo Pot's fire stage (GDD "POT upgrade stages") —
// client-side progress, no server field, so it survives a reload here.
// const LOGO_POT_KEY = "logo_pot";
const LOGO_POT_KEY = `${KEY_PREFIX}logo_pot`;
// Cleopatra's Fire: the Hold & Win's SETTLING step, kept until the room hands
// back — a reload before then replays the collection (the server's init
// carries nothing once the round is closed).
// const HOLD_AND_WIN_SETTLED_KEY = "hold_and_win_settled";
const HOLD_AND_WIN_SETTLED_KEY = `${KEY_PREFIX}hold_and_win_settled`;

/**
 * StorageManager — Handles all localStorage operations.
 *
 * Sections:
 *   1. Session   — Token validation, data clearing on player switch
 *   2. Settings  — Sound & UI preferences
 *   3. Base Game — Round persistence for reload recovery
 *   4. Bonus     — Mid-bonus snapshot for reload recovery
 */
class StorageManager {
  // ============================================
  // SESSION MANAGEMENT METHODS
  // ============================================

  /**
   * Get current session token from window.__OPTIONS__
   * Uses cache_player_id as the session identifier
   * @returns {string|null} Session token or null if not available
   */
  static getCurrentSessionToken() {
    return window.__OPTIONS__?.cache_player_id || null;
  }

  /**
   * Get stored session token from localStorage
   * @returns {string|null} Stored session token or null if not found
   */
  static getStoredSessionToken() {
    try {
      return localStorage.getItem(SESSION_KEY);
    } catch (error) {
      return null;
    }
  }

  /**
   * Save current session token to localStorage
   * @param {string} token - Session token to save
   */
  static saveSessionToken(token) {
    try {
      if (token) {
        localStorage.setItem(SESSION_KEY, token);
      }
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if current session matches stored session
   * @returns {boolean} True if session matches, false if new session or no stored session
   */
  static isSessionValid() {
    const currentToken = this.getCurrentSessionToken();
    const storedToken = this.getStoredSessionToken();

    // If no current token, can't validate
    if (!currentToken) {
      return false;
    }

    // If no stored token, treat as new session
    if (!storedToken) {
      return false;
    }

    // Compare tokens
    return currentToken === storedToken;
  }

  /**
   * Clear all session-specific data from localStorage.
   * Called when session token mismatch is detected (new player).
   * Removes base game round, bonus game round, and user preferences.
   *
   * EVERY round-scoped key belongs in here. A key left out survives a session
   * change and gets replayed into a different player's session — which for a
   * recovery record means restoring a feature that session never triggered.
   */
  static clearSessionData() {
    try {
      localStorage.removeItem(BASE_GAME_ROUND_KEY);
      localStorage.removeItem(BONUS_GAME_ROUND_KEY);
      localStorage.removeItem(FREE_GAME_ROUND_KEY);
      localStorage.removeItem(FREE_SPINS_ROUND_KEY);
      // A stale trigger win outlived a player switch and was read as the next
      // player's Total Win in WinDisplay. Every other round key was here; this
      // one was missed.
      localStorage.removeItem(TRIGGER_WIN_KEY);
      // The Pot's fire belongs to the player it was built by.
      localStorage.removeItem(LOGO_POT_KEY);
      localStorage.removeItem(HOLD_AND_WIN_SETTLED_KEY);
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  // ============================================
  // SOUND SETTINGS METHODS
  // ============================================

  /**
   * Load sound settings from localStorage
   * @returns {Object} Sound settings object with defaults if not found
   */
  static loadSoundSettings() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return this._getDefaultSoundSettings();
      }

      const parsed = JSON.parse(data);

      // Validate and return sound settings with defaults for missing values
      const soundSettings = parsed.sound || {};
      return {
        masterVolume: this._validateNumber(
          soundSettings.masterVolume,
          0.7,
          0,
          1,
        ),
        soundEffectsEnabled: this._validateBoolean(
          soundSettings.soundEffectsEnabled,
          true,
        ),
        backgroundMusicEnabled: this._validateBoolean(
          soundSettings.backgroundMusicEnabled,
          true,
        ),
        globalSoundDisabled: this._validateBoolean(
          soundSettings.globalSoundDisabled,
          false,
        ),
        currentBackgroundMusic: soundSettings.currentBackgroundMusic || null,
      };
    } catch (error) {
      return this._getDefaultSoundSettings();
    }
  }

  /**
   * Save sound settings to localStorage
   * @param {Object} settings - Sound settings object
   * @param {number} settings.masterVolume - Master volume (0-1)
   * @param {boolean} settings.soundEffectsEnabled - Sound effects enabled
   * @param {boolean} settings.backgroundMusicEnabled - Background music enabled
   * @param {boolean} settings.globalSoundDisabled - Global sound disabled
   */
  static saveSoundSettings(settings) {
    try {
      // Load existing data to preserve other settings categories
      const existingData = this._loadRawData();

      // Update sound settings
      existingData.sound = {
        masterVolume: settings.masterVolume,
        soundEffectsEnabled: settings.soundEffectsEnabled,
        backgroundMusicEnabled: settings.backgroundMusicEnabled,
        globalSoundDisabled: settings.globalSoundDisabled,
        currentBackgroundMusic: settings.currentBackgroundMusic || null,
      };

      // Save back to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load UI settings from localStorage
   * @returns {Object} UI settings object with defaults if not found
   */
  static loadUISettings() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return this._getDefaultUISettings();
      }

      const parsed = JSON.parse(data);
      const uiSettings = parsed.ui || {};

      // Resolve autoPlaySpeed: prefer stored string
      let autoPlaySpeed = "auto";
      if (typeof uiSettings.autoPlaySpeed === "string" &&
          ["auto", "quick", "turbo", "super_turbo"].includes(uiSettings.autoPlaySpeed)) {
        autoPlaySpeed = uiSettings.autoPlaySpeed;
      }

      return {
        turboSuggestionDeclined: this._validateBoolean(
          uiSettings.turboSuggestionDeclined,
          false,
        ),
        autoPlaySpeed,
      };
    } catch (error) {
      return this._getDefaultUISettings();
    }
  }

  /**
   * Save UI settings to localStorage
   * @param {Object} settings - UI settings object
   * @param {boolean} settings.turboSuggestionDeclined - Turbo suggestion declined
   * @param {string} settings.autoPlaySpeed - Auto play speed setting
   */
  static saveUISettings(settings) {
    try {
      const existingData = this._loadRawData();

      existingData.ui = {
        turboSuggestionDeclined: settings.turboSuggestionDeclined,
        autoPlaySpeed: settings.autoPlaySpeed || "auto",
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }


  // ============================================
  // BASE GAME ROUND STORAGE METHODS
  // ============================================

  /**
   * Save base game round state.
   * Called after every successful base game spin and on bet changes.
   * Stores reels, wins, and bet for reload recovery.
   *
   * @param {Object} roundData
   * @param {Object} roundData.round - Round data (reels, wins, win, winType, etc.)
   * @param {number} roundData.bet - Bet value used for this round
   */
  static saveBaseGameRound(roundData) {
    try {
      const round = roundData.round;
      const data = {
        round: {
          reels: round.reels,
          wins: round.wins,
          win: round.win,
          winType: round.winType || "",
          isAnimatedWin: round.isAnimatedWin || false,
          triggerWin: round.triggerWin || false,
          // Preserve the bet that produced this win inside the round.
          // When the user changes bet, the top-level `bet` is overwritten,
          // but round.bet stays intact so reload can use the correct bet
          // for win type calculations (win animation multiplier ratio).
          bet: round.bet || roundData.bet,
          // ── The settled reveal's own outcome ──
          //
          // A gamble scatter that has opened is not symbol 12 any more: the
          // cell is an open gold window with the awarded package inside it,
          // and the only thing that says WHICH package is this payload. Drop
          // it and a reload can rebuild the board but not that cell, so the
          // player comes back to a closed gold door where they left an open
          // window — which is exactly what happened after a bonus.
          //
          // Cheap to keep: three numbers and a position. The picture itself is
          // rebuilt from them at boot-warmed textures, not stored.
          gambleScatter: round.gambleScatter || null,
          // ── Where the mysteries dropped and what they became ──
          //
          // `storage.mystery_feature` is the only record of it: the board in
          // `reels` is the board AFTER the reveal, so from the ids alone a
          // reload cannot tell a symbol that was always there from one that
          // came out of a mystery. Without this those cells come back as bare
          // symbols and lose the frame the player is looking at.
          //
          // Kept whole rather than picked apart — it is one small object, and
          // the reader (mysteryStaging) owns its shape.
          storage: round.storage || null,
          // ── The numbers on its coins (2026-10-29, QA: "coin values change
          // after reloading") ── a plain coin's value is the client's own
          // (Games/Shared/coinValues), picked once per round; left out here,
          // a reload found none and picked new ones.
          coinValues: round.coinValues || null,
        },
        bet: roundData.bet,
      };

      localStorage.setItem(BASE_GAME_ROUND_KEY, JSON.stringify(data));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load base game round state.
   * Called during game initialization to restore reels and bet.
   * @returns {Object|null} { round, bet } or null if not found / corrupted
   */
  static loadBaseGameRound() {
    try {
      const data = localStorage.getItem(BASE_GAME_ROUND_KEY);
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);

      // Validate required fields to prevent loading corrupted data
      if (!parsed.round || parsed.bet === undefined) {
        this.clearBaseGameRound();
        return null;
      }

      return parsed;
    } catch (error) {
      // Clear corrupted data
      this.clearBaseGameRound();
      return null;
    }
  }

  /**
   * Clear base game round state.
   * Called to remove stale data (e.g. corrupted or session mismatch).
   */
  static clearBaseGameRound() {
    try {
      localStorage.removeItem(BASE_GAME_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if base game round data exists
   *
   * @returns {boolean} True if round data exists
   */
  static hasBaseGameRound() {
    try {
      const data = localStorage.getItem(BASE_GAME_ROUND_KEY);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Strip wins from the stored base game round.
   * Keeps reels, bet — clears wins, win, winType, isAnimatedWin, triggerWin.
   * Called when bonus game starts so reload won't replay base game win animations.
   */
  static stripBaseGameWins() {
    try {
      const stored = this.loadBaseGameRound();
      if (!stored || !stored.round) return;

      stored.round.wins = [];
      stored.round.win = 0;
      stored.round.winType = "";
      stored.round.isAnimatedWin = false;
      stored.round.triggerWin = false;

      localStorage.setItem(BASE_GAME_ROUND_KEY, JSON.stringify(stored));
    } catch (error) {
      // Fail silently
    }
  }

  // ============================================
  // BONUS GAME ROUND STORAGE METHODS
  // ============================================

  /**
   * Save bonus game round state (full reconstruction backup).
   * Called after each respin during the bonus feature.
   * Stores everything needed to reconstruct the bonus scene without server data.
   *
   * @param {Object} roundData
   * @param {Object} roundData.round - Current bonus round (reels, wins, win, storage)
   * @param {number} roundData.bet - Bet amount
   * @param {number} roundData.respinsLeft - Remaining respins
   * @param {number} roundData.respinsIssued - Total respins issued at bonus start
   * @param {number} roundData.totalWin - Accumulated bonus win
   * @param {number} roundData.coinCount - Current coin count on board
   * @param {string} roundData.respinGameType - Feature combination e.g. "DMC", "MC", "D"
   * @param {Array} roundData.initialRespinScreen - Bonus starting grid (never changes)
   * @param {Array} roundData.initialRespinValues - Bonus starting values (post-feature)
   * @param {Array} roundData.initialRespinOriginalValues - Pre-feature drawn values
   * @param {Array} roundData.initialEvents - Trigger activation events (m_reveals, d_doubles, etc.)
   * @param {Array} roundData.triggerCoordinates - Zeus coin positions from trigger spin
   * @param {Array} roundData.valuesGrid - Current mid-game coin values (storage.values)
   * @param {Array} roundData.originalValues - Pre-double values (storage.original_values)
   * @param {Array} roundData.prevScreen - Previous screen (storage.prev_screen)
   * @param {Array} roundData.screenProgress - Enriched event sequence for animations
   */
  static saveBonusGameRound(roundData) {
    try {
      const data = {
        // Core round
        round: {
          reels: roundData.round?.reels || [],
          wins: roundData.round?.wins || [],
          win: roundData.round?.win || 0,
          storage: roundData.round?.storage || null,
        },
        bet: roundData.bet,
        respinsLeft: roundData.respinsLeft ?? 0,
        respinsIssued: roundData.respinsIssued ?? 3,
        totalWin: roundData.totalWin ?? 0,
        coinCount: roundData.coinCount ?? 0,

        // Feature identity
        respinGameType: roundData.respinGameType || "",
        triggerCoordinates: roundData.triggerCoordinates || null,

        // Initial bonus state (frozen at trigger — never changes)
        initialRespinScreen: roundData.initialRespinScreen || null,
        initialRespinValues: roundData.initialRespinValues || null,
        initialRespinOriginalValues: roundData.initialRespinOriginalValues || null,
        initialEvents: roundData.initialEvents || [],

        // Current mid-game grids (updated each respin)
        valuesGrid: roundData.valuesGrid || null,
        originalValues: roundData.originalValues || null,
        prevScreen: roundData.prevScreen || null,

        // Enriched event sequence for animation replay
        screenProgress: roundData.screenProgress || [],

        // Accumulated flipped coin positions (persisted across respins)
        flippedCoinPositions: roundData.flippedCoinPositions || [],

        // Win limit flag — BE source of truth (outcome.storage.win_limit_reached)
        winLimitReached: roundData.winLimitReached || false,
      };

      localStorage.setItem(BONUS_GAME_ROUND_KEY, JSON.stringify(data));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load bonus game round state.
   * @returns {Object|null} Round data or null if not found
   */
  static loadBonusGameRound() {
    try {
      const data = localStorage.getItem(BONUS_GAME_ROUND_KEY);
      if (!data) return null;

      const parsed = JSON.parse(data);
      if (!parsed.round || parsed.bet === undefined) {
        this.clearBonusGameRound();
        return null;
      }
      return parsed;
    } catch (error) {
      this.clearBonusGameRound();
      return null;
    }
  }

  /**
   * Clear bonus game round state.
   * Called on first base game spin after bonus completes.
   */
  static clearBonusGameRound() {
    try {
      localStorage.removeItem(BONUS_GAME_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if bonus game round data exists.
   * @returns {boolean}
   */
  static hasBonusGameRound() {
    try {
      const data = localStorage.getItem(BONUS_GAME_ROUND_KEY);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  // ── Logo Pot (Cleopatra's Fire) — the fire stage and the points toward the
  // next one. Same shape as the trigger win's pair below.

  static saveLogoPot(pot) {
    try {
      localStorage.setItem(LOGO_POT_KEY, JSON.stringify(pot));
    } catch (error) {
      // Fail silently
    }
  }

  static loadLogoPot() {
    try {
      const data = localStorage.getItem(LOGO_POT_KEY);
      return data === null ? null : JSON.parse(data);
    } catch (error) {
      return null;
    }
  }

  // ── Hold & Win, settled but not yet collected on screen (Cleopatra's Fire) ──
  // Pot Collection's completion record, for H&W: written on the settling
  // respin, cleared when the room hands back. Its existence IS the owed
  // collection (StorageManager.hasBonusGameRound's rule).

  static saveHoldAndWinSettled(record) {
    try {
      localStorage.setItem(HOLD_AND_WIN_SETTLED_KEY, JSON.stringify(record));
    } catch (error) {
      // Fail silently
    }
  }

  static loadHoldAndWinSettled() {
    try {
      const data = localStorage.getItem(HOLD_AND_WIN_SETTLED_KEY);
      return data === null ? null : JSON.parse(data);
    } catch (error) {
      return null;
    }
  }

  static clearHoldAndWinSettled() {
    try {
      localStorage.removeItem(HOLD_AND_WIN_SETTLED_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  // ── Trigger Win (frozen at bonus trigger, never updated by respins) ──

  /**
   * Save the base game trigger win at bonus trigger time.
   * Frozen for the entire bonus — respins carry this value through.
   * @param {number} win - The base game win that triggered the bonus
   */
  static saveTriggerWin(win) {
    try {
      localStorage.setItem(TRIGGER_WIN_KEY, JSON.stringify(win));
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Load the frozen trigger win.
   * @returns {number} The trigger win or 0 if not found
   */
  static loadTriggerWin() {
    try {
      const data = localStorage.getItem(TRIGGER_WIN_KEY);
      if (data === null) return 0;
      return JSON.parse(data) || 0;
    } catch (error) {
      return 0;
    }
  }

  /** Clear frozen trigger win (error recovery → base after bonus ended elsewhere). */
  static clearTriggerWin() {
    try {
      localStorage.removeItem(TRIGGER_WIN_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  // ── Free spins round (Mad Labs) ──
  //
  // The counterpart of bonus_game_round, written the same way and for the same
  // reason: the server stops describing the round the moment it closes, and an
  // init during `freespins` sends `outcome.screen` null in the 0-played window.
  // So the whole round lives here — written on the trigger, rewritten on EVERY
  // free spin, and cleared only by the next BASE spin, exactly as Pot
  // Collection's record is.
  //
  // It carries BOTH halves, as saveBonusGameRound does: `triggerRound` is the
  // frozen entry board (Pot Collection's initialRespinScreen) and the rest is
  // the live state, so one record rebuilds the entry sequence, a mid-round
  // resume, and the outro.

  /**
   * @param {Object} data
   * @param {Object} data.round        last free-spin board (reels, wins, win)
   * @param {number} data.bet
   * @param {number} data.awarded      freespins_issued — GROWS mid-round
   * @param {number} data.played       issued - left
   * @param {number} data.totalWin     the round's running total (balance.game)
   * @param {number} data.level        room index
   * @param {number} data.tier         collector tier
   * @param {number} data.serverLevel  raw fs_state.level, drives the boxes
   * @param {number} data.packageTier  gamble package tier
   * @param {Object} data.triggerRound frozen entry board, with gambleScatter
   * @param {boolean} data.closed      the round is over and owes its outro
   */
  static saveFreeSpinsRound(data) {
    try {
      const record = {
        // ── The fields a WIN REPLAY needs, not just the ones a board needs ──
        //
        // This started as a copy of saveBonusGameRound, and that was wrong for
        // the last field. Pot Collection replays its outro from its event log,
        // so its record has no use for winType/triggerWin. Free spins replays
        // through useWinController, which is the BASE game's path — and
        // saveBaseGameRound keeps exactly these four for exactly this reason.
        //
        // Without `triggerWin` the win controller returns at
        // `!currentRound?.triggerWin` and nothing presents. That is not just a
        // missing replay: the round loop waits on `roundWin > 0 &&
        // !counterFinished`, and the release only ever comes from the
        // presentation, so the round hangs and the total win dialog never
        // opens either.
        round: data.round
          ? {
              reels: data.round.reels || [],
              wins: data.round.wins || [],
              win: data.round.win || 0,
              winType: data.round.winType || "",
              isAnimatedWin: data.round.isAnimatedWin || false,
              triggerWin: data.round.triggerWin || false,
              // useWinController dedupes by round id, and the reveal's
              // once-per-round guard compares it too. A restored round with no
              // id compares null to null and reads as "already presented".
              id: data.round.id ?? null,
              bet: data.round.bet ?? data.bet ?? null,
              storage: data.round.storage || null,
              coinValues: data.round.coinValues || null, // its coins' numbers (2026-10-29, as the base round)
            }
          : null,
        bet: data.bet ?? null,
        awarded: data.awarded ?? 0,
        played: data.played ?? 0,
        totalWin: data.totalWin ?? 0,
        level: data.level ?? 0,
        tier: data.tier ?? 0,
        serverLevel: data.serverLevel ?? 1,
        packageTier: data.packageTier ?? null,
        // Frozen at the trigger, never rewritten by a spin — the entry
        // sequence replays from this one.
        triggerRound: data.triggerRound || null,
        closed: data.closed === true,
      };
      localStorage.setItem(FREE_SPINS_ROUND_KEY, JSON.stringify(record));
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Merge into the existing record, so a spin does not have to restate the
   * frozen half. Creates it when absent.
   */
  static updateFreeSpinsRound(patch) {
    const existing = this.loadFreeSpinsRound() || {};
    this.saveFreeSpinsRound({ ...existing, ...patch });
  }

  /** @returns {Object|null} */
  static loadFreeSpinsRound() {
    try {
      const data = localStorage.getItem(FREE_SPINS_ROUND_KEY);
      if (!data) return null;
      return JSON.parse(data);
    } catch (error) {
      return null;
    }
  }

  /** The first-write discriminator, exactly as hasBonusGameRound is used. */
  static hasFreeSpinsRound() {
    try {
      return localStorage.getItem(FREE_SPINS_ROUND_KEY) !== null;
    } catch (error) {
      return false;
    }
  }

  static clearFreeSpinsRound() {
    try {
      localStorage.removeItem(FREE_SPINS_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }


  // ============================================
  // FREE ROUNDS PERSISTENCE METHODS
  // ============================================

  /**
   * Save free rounds state to localStorage.
   * Called on init when free rounds are active, and after each FR spin.
   *
   * @param {Object} data
   * @param {string} data.status - "active" | "played"
   * @param {number} data.betValue - Locked bet amount (subunits)
   * @param {number} data.performed - Rounds completed
   * @param {number} data.count - Total rounds granted
   * @param {number} data.win - Win amount (from balance.free_rounds)
   * @param {number} [data.betToRestore] - Original bet before FR started
   * @param {boolean} [data.lastRoundApplied] - True once `win` has been corrected
   *   to include the most recent round's own win (done by the live win-reveal,
   *   not at spin-response time — see updateFreeRoundsWin). False the instant a
   *   spin response is persisted, before its reveal has played. Preserved as-is
   *   if the caller doesn't pass it (e.g. init-time re-syncs of an ongoing FR).
   */
  static saveFreeRoundsRound(data) {
    try {
      // Preserve existing betToRestore if caller doesn't provide one —
      // it's set once at FR init and must survive all subsequent saves
      const existing = this.loadFreeRoundsRound();
      const toStore = {
        status: data.status,
        betValue: data.betValue,
        performed: data.performed,
        count: data.count,
        win: data.win ?? 0,
        betToRestore: data.betToRestore ?? existing?.betToRestore ?? null,
        lastRoundApplied: data.lastRoundApplied ?? existing?.lastRoundApplied ?? true,
      };
      localStorage.setItem(FREE_GAME_ROUND_KEY, JSON.stringify(toStore));
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Load free rounds state from localStorage.
   * @returns {Object|null} { status, betValue, performed, count, win, betToRestore } or null
   */
  static loadFreeRoundsRound() {
    try {
      const data = localStorage.getItem(FREE_GAME_ROUND_KEY);
      if (!data) return null;

      const parsed = JSON.parse(data);

      // Validate required fields
      if (!parsed.status || parsed.count === undefined) {
        this.clearFreeRoundsRound();
        return null;
      }

      return parsed;
    } catch (error) {
      this.clearFreeRoundsRound();
      return null;
    }
  }

  /**
   * Clear free rounds state from localStorage.
   */
  static clearFreeRoundsRound() {
    try {
      localStorage.removeItem(FREE_GAME_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if free rounds data exists in localStorage.
   * @returns {boolean}
   */
  static hasFreeRoundsRound() {
    try {
      return localStorage.getItem(FREE_GAME_ROUND_KEY) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Update only the win field in the stored free rounds data.
   * Called when displayedWin changes client-side (e.g. after bonus outro increment)
   * so that a reload before the next spin has the correct total.
   * Marks lastRoundApplied = true — this is the point where the round's own win
   * has actually been folded into the total (after its reveal played), so a
   * subsequent reload's withhold-then-replay logic can safely assume `win` is complete.
   * @param {number} win - New win amount to persist
   */
  static updateFreeRoundsWin(win) {
    try {
      const stored = this.loadFreeRoundsRound();
      if (!stored) return;
      stored.win = win;
      stored.lastRoundApplied = true;
      localStorage.setItem(FREE_GAME_ROUND_KEY, JSON.stringify(stored));
    } catch (error) {
      // Fail silently
    }
  }

  // ============================================
  // PRIVATE HELPER METHODS
  // ============================================

  /**
   * Load raw data from localStorage
   * @private
   */
  static _loadRawData() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      return {};
    }
  }

  /**
   * Get default sound settings
   * @private
   */
  static _getDefaultSoundSettings() {
    return {
      masterVolume: 0.7,
      soundEffectsEnabled: true,
      backgroundMusicEnabled: true,
      globalSoundDisabled: false,
    };
  }

  /**
   * Get default UI settings
   * @private
   */
  static _getDefaultUISettings() {
    return {
      turboSuggestionDeclined: false,
      autoPlaySpeed: "auto",
    };
  }

  /**
   * Validate a number value with min/max bounds
   * @private
   */
  static _validateNumber(value, defaultValue, min, max) {
    if (typeof value !== "number" || isNaN(value)) {
      return defaultValue;
    }
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Validate a boolean value
   * @private
   */
  static _validateBoolean(value, defaultValue) {
    return typeof value === "boolean" ? value : defaultValue;
  }
}

export default StorageManager;
